import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { DisclaimerModal, TermsOfServiceModal, PrivacyPolicyModal } from './LegalModals.jsx';
import { AvatarSelector, AnimalAvatar, ANIMAL_LIST } from './AnimalAvatars.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, emailService, FIREBASE_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase, getTenantUsersKey, resolveTenantId, setAuthClaims } from '../shared.jsx';
import { useIsAndroidWrapper } from '../TwaContext.jsx';
import AndroidSignupBlocked from './AndroidSignupBlocked.jsx';

// Mobile detection — used to switch signInWithPopup → signInWithRedirect
const isMobile = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && window.innerWidth < 1024);
};

// Embedded browser / WebView detection — Google blocks OAuth in these
const isEmbeddedBrowser = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|FB_IAB|Instagram|Messenger|LinkedIn|Twitter|Snapchat|Line\//i.test(ua) ||
    (ua.includes('wv') && /Android/i.test(ua));
};

function LoginView({ onLogin, pendingInvite }) {
  // S6: wrap onLogin to always stamp Firebase Auth custom claims before proceeding.
  const loginWithClaims = async (appUser) => {
    const firebaseUser = window.firebaseAuth?.currentUser;
    const identity = firebaseUser && await setAuthClaims(firebaseUser.uid);
    if (!identity || identity.id !== appUser?.id) {
      throw new Error('Your account could not be verified. Please sign in again or contact support.');
    }
    return onLogin({ ...appUser, ...identity });
  };
  // TWA gating: when inside the Android TWA wrapper, signup is blocked
  // (handled via AndroidSignupBlocked below). Login flows are unchanged.
  // Web users always get the original behavior.
  const inAndroidWrapper = useIsAndroidWrapper();
  const [mode, setMode] = useState(pendingInvite ? 'signup' : 'login'); // Default to signup if invited
  const [email, setEmail] = useState(pendingInvite?.email || '');
  const [name, setName] = useState('');
  const [role, setRole] = useState('mentee');
  const [avatar, setAvatar] = useState(() => ANIMAL_LIST[Math.floor(Math.random() * ANIMAL_LIST.length)]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteDetails, setInviteDetails] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showLoginTOS, setShowLoginTOS] = useState(false);
  const [showLoginPrivacy, setShowLoginPrivacy] = useState(false);
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [verificationCode, setVerificationCode] = useState(null);
  const [enteredCode, setEnteredCode] = useState('');

  // Handle authenticated user on mount — covers both:
  // 1. Mobile signInWithRedirect returning from Google OAuth
  // 2. Returning users whose Firebase Auth session persisted but RTDB session was empty
  // We check currentUser directly instead of getRedirectResult() because Firebase v9
  // compat clears the redirect result after onAuthStateChanged consumes it.
  useEffect(() => {
    const handleAuthenticatedUser = async () => {
      if (!window.firebaseAuth) await initFirebase();
      const currentUser = window.firebaseAuth?.currentUser;
      // Only auto-login real OAuth users (Google), not anonymous sessions
      if (currentUser && !currentUser.isAnonymous) {
        setLoading(true);
        try {
          await handleFirebaseUser(currentUser);
          return;
        } catch (err) {
          console.error('[LoginView] Auto-login failed:', err);
          setError('Sign-in failed. Please try again.');
        }
        setLoading(false);
      }
      // Also try getRedirectResult as a fallback
      try {
        const result = await window.firebaseAuth.getRedirectResult();
        sessionStorage.removeItem('auth-redirect-pending');
        if (result?.user) {
          setLoading(true);
          await handleFirebaseUser(result.user);
          return;
        }
      } catch (err) {
        if (err.code !== 'auth/popup-closed-by-user') {
          console.error('Redirect sign-in error:', err);
        }
      }
    };
    handleAuthenticatedUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load invite details when component mounts
  useEffect(() => {
    const loadInviteDetails = async () => {
      if (pendingInvite?.conflictId) {
        try {
          const conflict = await storage.get(`conflict:${pendingInvite.conflictId}`);
          if (conflict) {
            setInviteDetails({
              title: conflict.title,
              description: conflict.problemStatement?.what,
              inviterName: conflict.mentees?.find(m => m.id === conflict.createdBy)?.name || 'Someone'
            });
          }
        } catch (error) {
          console.error('Error loading invite details:', error);
        }
      }
    };
    loadInviteDetails();
  }, [pendingInvite]);

  // The server locates existing accounts across tenants and owns signup/linkage.
  const prepareAccount = async (firebaseUser, allowCreate) => {
    const token = await firebaseUser.getIdToken();
    const response = await fetch(`https://us-central1-${FIREBASE_CONFIG.projectId}.cloudfunctions.net/prepareVerifiedAccount`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ allowCreate: allowCreate && !inAndroidWrapper, name: name.trim(), avatar,
        conflictId: pendingInvite?.conflictId || null }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Your account could not be verified.');
    if (window.firebaseAuth.currentUser !== firebaseUser) throw new Error('Your sign-in has changed. Please try again.');
    await loginWithClaims(result.profile);
  };

  const handleFirebaseUser = async firebaseUser => {
    try {
      await prepareAccount(firebaseUser, true);
    } catch (err) {
      setError(err.message || 'Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Google Sign-In — popup-first with redirect fallback
  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      if (!window.firebaseAuth) await initFirebase();
      const provider = new window.firebase.auth.GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      try {
        // Try popup first (works on desktop + most mobile browsers)
        const result = await window.firebaseAuth.signInWithPopup(provider);
        await handleFirebaseUser(result.user);
      } catch (popupErr) {
        // If popup was blocked or failed on mobile, fall back to redirect
        if (popupErr.code === 'auth/popup-blocked' ||
            popupErr.code === 'auth/cancelled-popup-request' ||
            popupErr.code === 'auth/operation-not-supported-in-this-environment') {
          sessionStorage.setItem('auth-redirect-pending', 'google');
          await window.firebaseAuth.signInWithRedirect(provider);
          return; // Page will redirect to Google
        }
        throw popupErr; // Re-throw other errors
      }
    } catch (err) {
      console.error('Google sign-in error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        // User closed popup, not an error
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized for sign-in. Please add it in Firebase Console → Authentication → Settings → Authorized domains.');
      } else {
        setError('Google sign-in failed. Please try again or use email.');
      }
    }
    setLoading(false);
  };

  // Email sign-in / sign-up: go straight to verification code
  // (Firebase email link requires specific console config that may not be enabled)
  const handleEmailLink = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');

    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    // For signup mode, require name and terms agreement
    if (mode === 'signup') {
      if (!name.trim()) {
        setError('Please enter your name.');
        return;
      }
      if (!agreedToTerms) {
        setError('Please agree to the Disclaimer & User Agreement.');
        return;
      }
    }

    setLoading(true);
    await handleEmailVerificationCode();
    setLoading(false);
  };

  const callEmailAuth = async (action, payload) => {
    const response = await fetch(`https://us-central1-${FIREBASE_CONFIG.projectId}.cloudfunctions.net/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Email verification failed.');
    return result;
  };

  // Only the server generates and stores the code. The browser holds its identifier.
  const handleEmailVerificationCode = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await callEmailAuth('requestEmailSignIn', { email: email.trim().toLowerCase() });
      setVerificationCode({ ...result, emailSent: true });
      setEnteredCode('');
      setShowVerificationInput(true);
    } catch (err) {
      setError(err.message || 'Failed to start email verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify the code the user entered
  const handleVerifyCode = async () => {
    setError('');
    if (!enteredCode.trim()) {
      setError('Please enter the verification code.');
      return;
    }
    setLoading(true);
    try {
      const verified = await callEmailAuth('verifyEmailSignIn', {
        email: email.trim().toLowerCase(),
        challengeId: verificationCode?.challengeId,
        code: enteredCode.trim(),
      });
      if (!window.firebaseAuth) await initFirebase();
      await window.firebaseAuth.signInWithCustomToken(verified.token);

      await prepareAccount(window.firebaseAuth.currentUser, mode === 'signup');
    } catch (err) {
      console.error('Code verification error:', err);
      setError(err.message || 'Verification failed. Please try again.');
    }
    setLoading(false);
  };

  // Legacy and new accounts both prove control of the email address.
  const handleDirectEmailLogin = async () => {
    setError('');
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    await handleEmailVerificationCode();
    setLoading(false);
  };

  if (showVerificationInput) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img 
                src={OWL_LOGO} 
                alt="ArguMentor Owl" 
                className="w-24 h-24 drop-shadow-lg"
                style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.15))' }}
              />
            </div>
            <h1 className="text-5xl font-light text-stone-800 mb-2 tracking-tight">
              Enter Verification Code
            </h1>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-stone-200 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-amber-700" />
            </div>
            <p className="text-stone-700 mb-2">
              Enter the 6-digit code for:
            </p>
            <p className="text-amber-700 font-medium text-lg mb-2">{email}</p>
            
            {verificationCode && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" role="status">
                {verificationCode.emailSent ? (
                  <>
                    <p className="text-sm text-amber-700 font-medium">A verification code has been sent to your email.</p>
                    <p className="text-xs text-amber-700 mt-1">Check your inbox (and spam folder) for the code.</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-red-700 font-medium">We were unable to send the verification email.</p>
                    <p className="text-xs text-red-700 mt-1">Please try again or use Google sign-in instead.</p>
                  </>
                )}
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <input
              type="text"
              aria-label="Verification code"
              value={enteredCode}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                setEnteredCode(val);
              }}
              onKeyDown={(e) => e.key === 'Enter' && enteredCode.length === 6 && handleVerifyCode()}
              className="w-full px-4 py-4 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-center text-2xl tracking-[0.4em] font-mono mb-4"
              placeholder="000000"
              maxLength={6}
              autoFocus
            />

            <div className="space-y-3">
              <button
                onClick={handleVerifyCode}
                disabled={loading || enteredCode.length !== 6}
                className="w-full bg-amber-700 hover:bg-amber-800 disabled:bg-amber-400 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </button>
              <button
                onClick={() => { 
                  setShowVerificationInput(false); 
                  setVerificationCode(null);
                  setEnteredCode('');
                  setError(''); 
                }}
                className="w-full py-3 border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-50 font-medium transition-colors"
              >
                ← Go back
              </button>
              <button
                onClick={handleEmailVerificationCode}
                disabled={loading}
                className="w-full py-3 text-amber-700 hover:text-amber-800 font-medium transition-colors text-sm"
              >
                {loading ? 'Sending...' : 'Get a new code'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Invitation Banner */}
        {pendingInvite && (
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-amber-800 mb-1">You've Been Invited!</h3>
                {inviteDetails ? (
                  <>
                    <p className="text-sm text-amber-700 mb-2">
                      <strong>{inviteDetails.inviterName}</strong> has invited you to help resolve a conflict:
                    </p>
                    <p className="text-sm text-amber-900 font-medium">"{inviteDetails.title}"</p>
                  </>
                ) : (
                  <p className="text-sm text-amber-700">
                    Someone has invited you to help resolve a conflict. Sign up or sign in to view it.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src={OWL_LOGO} 
              alt="ArguMentor Owl" 
              className="w-24 h-24 drop-shadow-lg"
              style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.15))' }}
            />
          </div>
          <h1 className="text-5xl font-light text-stone-800 mb-2 tracking-tight">
            The ArguMentor
          </h1>
          <p className="text-stone-500 font-light text-lg">
            Resolve conflicts with clarity
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-stone-200">
          {/* Tab Switcher */}
          <div className="flex gap-2 mb-6 bg-stone-100 p-1 rounded-lg">
            <button
              onClick={() => {
                setMode('login');
                setError('');
              }}
              className={`flex-1 py-2 rounded-md font-medium transition-colors ${
                mode === 'login'
                  ? 'bg-white text-amber-700 shadow-sm'
                  : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => {
                setMode('signup');
                setError('');
              }}
              className={`flex-1 py-2 rounded-md font-medium transition-colors ${
                mode === 'signup'
                  ? 'bg-white text-amber-700 shadow-sm'
                  : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              Sign Up
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Embedded browser warning */}
          {isEmbeddedBrowser() && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm font-medium mb-1">Open in your browser</p>
              <p className="text-amber-700 text-xs">
                Google sign-in doesn't work inside app browsers (Facebook, Messenger, Instagram, etc.).
                Tap the <strong>menu (⋮)</strong> or <strong>compass icon</strong> and choose <strong>"Open in Browser"</strong>, or copy the link to Chrome/Safari.
              </p>
            </div>
          )}

          {/* Social Sign-In Buttons — hidden on Android during signup
              to keep new accounts off the device (web-only billing model) */}
          {!(inAndroidWrapper && mode === 'signup') && (
          <>
          <div className="space-y-3 mb-6">
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors font-medium text-stone-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-stone-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-stone-500">or continue with email</span>
            </div>
          </div>
          </>
          )}

          {/* Sign In Form (email only) */}
          {mode === 'login' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-stone-700 mb-2">
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleDirectEmailLogin()}
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <button
                onClick={handleDirectEmailLogin}
                disabled={loading}
                className="w-full bg-amber-700 hover:bg-amber-800 disabled:bg-amber-400 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                {loading ? 'Sending...' : 'Continue with Email'}
              </button>

              <p className="text-xs text-stone-500 text-center">
                We'll verify your email with a one-time code, no password needed
              </p>
            </div>
          )}

          {/* Sign Up Form — replaced with AndroidSignupBlocked on Android
              TWA so new accounts are created on the web (web-only billing) */}
          {mode === 'signup' && inAndroidWrapper && (
            <AndroidSignupBlocked onSwitchToLogin={() => { setMode('login'); setError(''); }} />
          )}
          {mode === 'signup' && !inAndroidWrapper && (
            <div className="space-y-4">
              <div>
                <label htmlFor="signup-name" className="block text-sm font-medium text-stone-700 mb-2">
                  Full Name
                </label>
                <input
                  id="signup-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Your name"
                  required
                />
              </div>

              <AvatarSelector selectedAnimal={avatar} onSelect={setAvatar} compact={true} />

              <div>
                <label htmlFor="signup-email" className="block text-sm font-medium text-stone-700 mb-2">
                  Email
                </label>
                <input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200">
                <input
                  type="checkbox"
                  id="agree-terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-amber-700 border-stone-300 rounded focus:ring-amber-500 flex-shrink-0"
                />
                <label htmlFor="agree-terms" className="text-sm text-stone-600">
                  I have read and agree to the{' '}
                  <button 
                    type="button"
                    onClick={() => setShowDisclaimer(true)} 
                    className="text-amber-700 hover:text-amber-800 underline font-medium"
                  >
                    Disclaimer & User Agreement
                  </button>
                  . I understand that The ArguMentor is a conflict resolution tool and does not provide 
                  mental health counseling or professional advice.
                </label>
              </div>

              <button
                onClick={handleEmailLink}
                disabled={loading || !agreedToTerms}
                className="w-full bg-amber-700 hover:bg-amber-800 disabled:bg-amber-400 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                {loading ? 'Sending...' : 'Continue with Email'}
              </button>

              <p className="text-xs text-stone-500 text-center">
                We'll verify your email with a one-time code, no password needed
              </p>
            </div>
          )}
        </div>

        <div className="text-center mt-6 mb-2">
          <a
            href="https://theargumentor.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-amber-700 hover:text-amber-800 hover:underline"
          >
            Curious? Read all about The ArguMentor &rarr;
          </a>
        </div>

        <div className="text-center text-stone-500 text-sm mt-2 flex flex-wrap justify-center gap-x-1 gap-y-1">
          <a href="https://www.4hci.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber-700 transition-colors">The ArguMentor, LLC</a>
          <span>·</span>
          <button onClick={() => setShowDisclaimer(true)} className="hover:text-amber-700 transition-colors">Disclaimer</button>
          <span>·</span>
          <button onClick={() => setShowLoginTOS(true)} className="hover:text-amber-700 transition-colors">Terms of Service</button>
          <span>·</span>
          <button onClick={() => setShowLoginPrivacy(true)} className="hover:text-amber-700 transition-colors">Privacy Policy</button>
        </div>
      </div>
      <DisclaimerModal isOpen={showDisclaimer} onClose={() => setShowDisclaimer(false)} />
      <TermsOfServiceModal isOpen={showLoginTOS} onClose={() => setShowLoginTOS(false)} />
      <PrivacyPolicyModal isOpen={showLoginPrivacy} onClose={() => setShowLoginPrivacy(false)} />
    </div>
  );
}

// User Management View Component (Admin Only)

export default LoginView;
